import { Suspense, lazy } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { datiImpresaNellIscrizione, invitiAllAcquistoNellApp } from "./lib/iscrizione";
import { Loader2 } from "lucide-react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppShell } from "./components/layout/AppShell";
import { AuthProvider } from "./hooks/useAuth";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { AdminRoute } from "./components/auth/AdminRoute";
import { RitornoAccessoNativo } from "./components/auth/RitornoAccessoNativo";

// La chat è il prodotto: si carica subito, senza passare da uno spinner.
import Chat from "./pages/Chat.tsx";
import Login from "./pages/Login.tsx";

/**
 * Tutto il resto arriva a richiesta.
 * L'installatore apre l'app in cantiere, spesso in 4G ballerino: non ha senso
 * fargli scaricare certificato di conformità, calcolatori e admin per fare una
 * domanda. Ogni pagina diventa un file a sé, scaricato solo quando serve.
 */
const Home = lazy(() => import("./pages/Home.tsx"));
const History = lazy(() => import("./pages/History.tsx"));
const Settings = lazy(() => import("./pages/Settings.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Signup = lazy(() => import("./pages/Signup.tsx"));
const RichiediAccesso = lazy(() => import("./pages/RichiediAccesso.tsx"));
// La pagina delle licenze (25/09): solo sul sito, nessun link dall'app nativa.
const Licenza = lazy(() => import("./pages/Licenza.tsx"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail.tsx"));
const AuthCallback = lazy(() => import("./pages/AuthCallback.tsx"));
const Admin = lazy(() => import("./pages/Admin.tsx"));
const Welcome = lazy(() => import("./pages/Welcome.tsx"));
const CompletaProfilo = lazy(() => import("./pages/CompletaProfilo.tsx"));
const Privacy = lazy(() => import("./pages/Privacy.tsx"));
const Terms = lazy(() => import("./pages/Terms.tsx"));
const EliminaAccount = lazy(() => import("./pages/EliminaAccount.tsx"));
const Aliases = lazy(() => import("./pages/Aliases.tsx"));
const Strumenti = lazy(() => import("./pages/Strumenti.tsx"));
const Conformita = lazy(() => import("./pages/Conformita.tsx"));
const Alimentazione = lazy(() => import("./pages/tools/Alimentazione.tsx"));
const CadutaTensione = lazy(() => import("./pages/tools/CadutaTensione.tsx"));
const ArchiviazioneVideo = lazy(() => import("./pages/tools/ArchiviazioneVideo.tsx"));
const ValutazioneRischio = lazy(() => import("./pages/tools/ValutazioneRischio.tsx"));
const CartelloGDPR = lazy(() => import("./pages/tools/CartelloGDPR.tsx"));
const CartelloStampa = lazy(() => import("./pages/CartelloStampa.tsx"));
const ScaricaApp = lazy(() => import("./pages/ScaricaApp.tsx"));
const BancoChat = lazy(() => import("./pages/BancoChat.tsx"));
const BancoOperatore = lazy(() => import("./pages/BancoOperatore.tsx"));

const queryClient = new QueryClient();

/**
 * Gli strumenti di calcolo: sull'iPhone liberi anche senza account (03/10),
 * cosi' chi trova l'app sullo store la usa subito. Altrove restano dietro
 * l'accesso. Usano il profilo solo se c'e' (useProfiloOpzionale).
 */
function StrumentiLiberi({ children }: { children: JSX.Element }) {
  return datiImpresaNellIscrizione() ? <ProtectedRoute>{children}</ProtectedRoute> : children;
}

/** Mostrato mentre una pagina viene scaricata. Deve essere leggero e discreto. */
function Caricamento() {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center bg-background">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Sonner />
        {/* La fascia dell'orologio ha un fondo suo, in tutta l'app: il
            contenuto che scorre non ci passa piu' sotto (screenshot di
            Apple, 29/09: «Partita IVA» sopra l'ora). Sul sito e' alta 0. */}
        <div className="velo-barra-stato" aria-hidden="true" />
        <BrowserRouter>
          {/* Ascolta il rientro dal login Google/Apple quando si e' dentro
              l'app nativa; sul web non fa niente. */}
          <RitornoAccessoNativo />
          <Suspense fallback={<Caricamento />}>
          <Routes>
            <Route path="/" element={<Home />} />
            {/* /splash non esiste piu' (06/10/2026, codice morto): un vecchio collegamento porta alla home. */}
            <Route path="/splash" element={<Navigate to="/" replace />} />
            <Route path="/signup" element={<Signup />} />
            {/* Sull'iPhone la richiesta di attivazione non esiste (Apple 3.1.1,
                29/09): è un invito a comprare fuori. Chi ci arriva torna alla chat. */}
            <Route
              path="/richiedi-accesso"
              element={invitiAllAcquistoNellApp() ? <RichiediAccesso /> : <Navigate to="/chat" replace />}
            />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route
              path="/completa-profilo"
              element={
                <ProtectedRoute>
                  <CompletaProfilo />
                </ProtectedRoute>
              }
            />
            {/* Pubblica di proposito: e' la via di stampa dall'app nativa,
                i dati viaggiano nell'indirizzo e non toccano il database. */}
            <Route path="/cartello-stampa" element={<CartelloStampa />} />
            {/* La pagina «Scarica l'app»: la porta di firme email, QR del banco
                e newsletter (05/10). Pubblica: chi arriva non ha ancora l'account. */}
            <Route path="/app" element={<ScaricaApp />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/elimina-account" element={<EliminaAccount />} />
            <Route path="/aliases" element={<Aliases />} />
            <Route
              path="/welcome"
              element={
                <ProtectedRoute>
                  <Welcome />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <Admin />
                </AdminRoute>
              }
            />
            {/* Gli strumenti stanno fuori dall'AppShell: hanno un loro layout
                a tutta pagina, e le pagine stampabili non devono ereditare la
                navigazione (finirebbe nel PDF). */}
            <Route
              path="/licenza"
              element={
                <ProtectedRoute>
                  <Licenza />
                </ProtectedRoute>
              }
            />
            <Route
              path="/conformita"
              element={
                <ProtectedRoute>
                  <Conformita />
                </ProtectedRoute>
              }
            />
            <Route
              path="/strumenti"
              element={
                <StrumentiLiberi>
                  <Strumenti />
                </StrumentiLiberi>
              }
            />
            {/* Il filo col banco Datacom: layout suo, come gli strumenti. */}
            <Route
              path="/banco"
              element={
                <ProtectedRoute>
                  <BancoChat />
                </ProtectedRoute>
              }
            />
            {/* La postazione dell'ufficio (info@datacomtecnologie.it). */}
            <Route
              path="/banco-operatore"
              element={
                <ProtectedRoute>
                  <BancoOperatore />
                </ProtectedRoute>
              }
            />
            <Route
              path="/strumenti/alimentazione"
              element={
                <StrumentiLiberi>
                  <Alimentazione />
                </StrumentiLiberi>
              }
            />
            <Route
              path="/strumenti/cavo"
              element={
                <StrumentiLiberi>
                  <CadutaTensione />
                </StrumentiLiberi>
              }
            />
            <Route
              path="/strumenti/video"
              element={
                <StrumentiLiberi>
                  <ArchiviazioneVideo />
                </StrumentiLiberi>
              }
            />
            <Route
              path="/strumenti/rischio"
              element={
                <StrumentiLiberi>
                  <ValutazioneRischio />
                </StrumentiLiberi>
              }
            />
            <Route
              path="/strumenti/gdpr"
              element={
                <StrumentiLiberi>
                  <CartelloGDPR />
                </StrumentiLiberi>
              }
            />
            <Route
              element={
                <ProtectedRoute>
                  <AppShell />
                </ProtectedRoute>
              }
            >
              <Route path="/chat" element={<Chat />} />
              <Route path="/chat/:id" element={<Chat />} />
              <Route path="/history" element={<History />} />
              <Route path="/settings" element={<Settings />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
